import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';
import Button from '../../ui/Button';
import { COPY } from './playlistCopy';

const POPOVER_WIDTH = 288;
const GAP = 8;
const EDGE = 16;

// Room a three-button popover needs above its anchor; with less it opens below.
const ROOM_ABOVE = 200;

const placement = (rect) => {
  if (!rect) return {};
  const left = Math.min(
    Math.max(rect.left + rect.width / 2 - POPOVER_WIDTH / 2, EDGE),
    document.documentElement.clientWidth - POPOVER_WIDTH - EDGE,
  );
  if (rect.top < ROOM_ABOVE) return { left, top: rect.bottom + GAP };
  return { left, bottom: document.documentElement.clientHeight - rect.top + GAP };
};

/**
 * Confirmation popover anchored above a rect (a tile or the Clear button),
 * portalled to <body>: the dock's backdrop-filter would otherwise become the
 * containing block of this fixed element.
 * Focus starts on the confirm button; Escape or an outside click cancels.
 */
export default function TileConfirmPopover({
  isOpen,
  anchorRect,
  message,
  confirmLabel = COPY.remove,
  confirmVariant = 'danger',
  secondaryAction = null,
  onConfirm,
  onCancel,
  testId = 'playlist-item-remove-confirm',
}) {
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const focusFrame = requestAnimationFrame(() => popoverRef.current?.querySelector('[data-confirm]')?.focus());
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
      } else if (event.key === 'Enter' && event.target?.hasAttribute?.('data-confirm')) {
        event.preventDefault();
        onConfirm();
      }
    };
    const handlePointer = (event) => {
      if (!popoverRef.current?.contains(event.target)) onCancel();
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('pointerdown', handlePointer);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('pointerdown', handlePointer);
    };
  }, [isOpen, onCancel, onConfirm]);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={popoverRef}
          initial={{ opacity: 0, y: 8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
          role="alertdialog"
          aria-label={message}
          data-testid={testId}
          className="fixed z-50 w-72 max-w-[calc(100vw-2rem)] rounded-2xl bg-white dark:bg-elevated-dark-1 border border-gray-200 dark:border-white/10 shadow-soft-lg p-4"
          style={placement(anchorRect)}
        >
          <div className="flex items-start gap-3 mb-3">
            <div className={`flex-shrink-0 p-1.5 rounded-lg ${confirmVariant === 'danger'
              ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
              : 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'}`}
            >
              <AlertTriangle className="w-4 h-4" />
            </div>
            <p className="text-body-sm font-semibold text-gray-900 dark:text-gray-100 min-w-0">{message}</p>
          </div>
          {secondaryAction ? (
            <div className="flex flex-col gap-2">
              <Button data-confirm onClick={onConfirm} variant={confirmVariant} size="sm" className="w-full">
                {confirmLabel}
              </Button>
              <Button onClick={secondaryAction.onClick} variant="secondary" size="sm" className="w-full" data-testid={secondaryAction.testId}>
                {secondaryAction.label}
              </Button>
              <Button onClick={onCancel} variant="ghost" size="sm" className="w-full">
                {COPY.cancel}
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button onClick={onCancel} variant="secondary" size="sm" className="flex-1">
                {COPY.cancel}
              </Button>
              <Button data-confirm onClick={onConfirm} variant={confirmVariant} size="sm" className="flex-1">
                {confirmLabel}
              </Button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
